import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "shared";

import {
  addTravelerHotels,
  removeTraveller,
  resetTravelerHotels,
} from "shared/data/actions/cbt";
import { setHotelPassengers } from "shared/data/actions/search";
import { RoomPassengers } from "shared/types/hotels";

import { calculateTotalTravelers } from "../../web/lib/Hotels/calculateTotalTravelers";

type MaxTravelersErrorId = "Hotels.maxPassengersError";
type MaxRoomOccupancyErrorId = "Hotels.maxRoomOccupancyError";
type NeedMoreAdultsErrorId = "PropertiesSelect.needMoreAdultsThanChildren";

interface UseHotelRoomManagementProps {
  searchHotelsRooms: {
    [key: number]: {
      ADT: number;
      CHD: number;
      guests: number;
      childAges: number[];
    };
  };
  hotelMaxRooms?: number | string;
  hotelSearchSettings?: {
    RoomGuestLimits: {
      GuestLimit: Array<{
        Code: string;
        MaxAge?: number;
        LessOrEqualToAdults?: string;
      }>;
    };
    RoomOccupancy?: {
      Max: string;
    };
    TotalGuestCount?: {
      Max: string;
    };
  };
  setErrorMessage: (message: string | null) => void;
  isCbtEnabled: boolean;
  hotelRoomCbtAssignments?: {
    hotelRoomAssignments: {
      [key: number]: {
        [key: string]: number;
      };
    };
  };
}

interface UseHotelRoomManagementReturn {
  rooms: RoomPassengers[];
  collapsedStates: boolean[];
  maxTravelersError: MaxTravelersErrorId | null;
  maxRoomOccupancyError: MaxRoomOccupancyErrorId | null;
  maxRoomOccupancyErrorRoomIndex: number | null;
  needMoreAdultsError: NeedMoreAdultsErrorId | null;
  roomError: number | null;
  handleAddPassenger: (roomIndex: number, travelerCode: string) => void;
  handleRemovePassenger: (roomIndex: number, travelerCode: string) => void;
  handleChildAgeChange: (
    roomIndex: number,
    childIndex: number,
    age: number
  ) => void;
  resetRoomsToDefault: () => void;
  addRoom: () => void;
  removeRoom: (roomIndexToRemove: number) => void;
  toggleRoomCollapse: (indexToToggle: number) => void;
  isDefaultState: boolean;
}

export const useHotelRoomManagement = ({
  hotelMaxRooms,
  hotelSearchSettings,
  setErrorMessage,
  isCbtEnabled,
  hotelRoomCbtAssignments,
}: UseHotelRoomManagementProps): UseHotelRoomManagementReturn => {
  const dispatch = useDispatch();

  const searchHotelsRooms = useSelector(
    (state: AppState) => state.search.searchHotels
  )?.rooms as {
    [key: number]: {
      ADT: number;
      CHD: number;
      guests: number;
      childAges: number[];
    };
  };

  const isDefaultStateCbt =
    (Object.keys(searchHotelsRooms).length === 1 &&
      searchHotelsRooms[0].ADT === 0 &&
      searchHotelsRooms[0].CHD === 0) ||
    searchHotelsRooms[0].guests === 0;

  const transformReduxStateToComponentState = useCallback(
    (reduxRooms: typeof searchHotelsRooms = {}): RoomPassengers[] => {
      const transformed = Object.entries(reduxRooms)
        .sort(([keyA], [keyB]) => parseInt(keyA, 10) - parseInt(keyB, 10))
        .map(([, roomData]) => {
          const childAges = Array.isArray(roomData.childAges)
            ? [...roomData.childAges]
            : [];
          return {
            ADT: roomData.ADT || 0,
            CHD: roomData.CHD || 0,
            guests: roomData.guests || 0,
            childAges,
          };
        });
      if (transformed.length === 0) {
        return [
          { ADT: !isCbtEnabled ? 1 : 0, CHD: 0, guests: 0, childAges: [] },
        ];
      }
      return transformed;
    },
    [isCbtEnabled]
  );

  const initialRoomsFromRedux = useMemo(
    () => transformReduxStateToComponentState(searchHotelsRooms),
    [searchHotelsRooms, transformReduxStateToComponentState]
  );

  const [rooms, setRooms] = useState<RoomPassengers[]>(initialRoomsFromRedux);
  const [collapsedStates, setCollapsedStates] = useState<boolean[]>(() =>
    initialRoomsFromRedux.map((_, index) => index !== 0)
  );

  const [
    maxTravelersError,
    setMaxTravelersError,
  ] = useState<MaxTravelersErrorId | null>(null);

  const [
    maxRoomOccupancyError,
    setMaxRoomOccupancyError,
  ] = useState<MaxRoomOccupancyErrorId | null>(null);
  const [
    maxRoomOccupancyErrorRoomIndex,
    setMaxRoomOccupancyErrorRoomIndex,
  ] = useState<number | null>(null);

  const [
    needMoreAdultsError,
    setNeedMoreAdultsError,
  ] = useState<NeedMoreAdultsErrorId | null>(null);

  const [roomError, setRoomError] = useState<number | null>(null);

  useEffect(() => {
    const currentTotalTravelers = calculateTotalTravelers(
      rooms,
      hotelRoomCbtAssignments as any
    );

    const totalGuestCountMax = Number(
      hotelSearchSettings?.TotalGuestCount?.Max
    );

    if (totalGuestCountMax && currentTotalTravelers >= totalGuestCountMax) {
      setMaxTravelersError("Hotels.maxPassengersError");
    } else if (
      (!totalGuestCountMax || currentTotalTravelers < totalGuestCountMax) &&
      maxTravelersError
    ) {
      setMaxTravelersError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, hotelRoomCbtAssignments, maxTravelersError, searchHotelsRooms]);

  const handleAddPassenger = (roomIndex: number, travelerCode: string) => {
    if (maxTravelersError) {
      setMaxTravelersError("Hotels.maxPassengersError");
      return;
    }

    setRooms((currentRooms) => {
      const updatedRooms = [...currentRooms];
      const currentRoom = { ...updatedRooms[roomIndex] };
      const currentValue = (currentRoom[travelerCode] as number) || 0;
      const newValue = currentValue + 1;

      const roomOccupancyMax = Number(
        hotelSearchSettings?.RoomOccupancy?.Max ?? 9
      );
      const currentRoomTotal =
        (currentRoom.ADT || 0) +
        (currentRoom.CHD || 0) +
        (currentRoom.guests || 0) +
        (Object.values(
          hotelRoomCbtAssignments?.hotelRoomAssignments?.[roomIndex] || {}
        ).length || 0);

      if (currentRoomTotal + 1 > roomOccupancyMax) {
        setMaxRoomOccupancyError("Hotels.maxRoomOccupancyError");
        setMaxRoomOccupancyErrorRoomIndex(roomIndex);
        return currentRooms;
      }

      const currentTotalTravelers = calculateTotalTravelers(
        currentRooms,
        hotelRoomCbtAssignments
      );
      const totalGuestCountMax = Number(
        hotelSearchSettings?.TotalGuestCount?.Max
      );

      if (
        totalGuestCountMax &&
        currentTotalTravelers + 1 > totalGuestCountMax
      ) {
        setMaxTravelersError("Hotels.maxPassengersError");
        return currentRooms;
      }

      const lessOrEqualToAdults =
        hotelSearchSettings?.RoomGuestLimits?.GuestLimit.find(
          (limit) => limit.Code === "CHD"
        )?.LessOrEqualToAdults === "true" || false;

      const childrenDefaultAge = lessOrEqualToAdults ? 10 : 0;

      updatedRooms[roomIndex] = {
        ...currentRoom,
        [travelerCode]: newValue,
        ...(travelerCode === "CHD" && {
          childAges: [
            ...(currentRoom.childAges || []),
            ...(newValue > currentValue ? [childrenDefaultAge] : []),
          ].slice(0, newValue),
        }),
      };
      setMaxTravelersError(null);
      setMaxRoomOccupancyError(null);
      setMaxRoomOccupancyErrorRoomIndex(null);
      return updatedRooms;
    });
  };

  const handleRemovePassenger = (roomIndex: number, travelerCode: string) => {
    setRooms((currentRooms) => {
      const updatedRooms = [...currentRooms];
      const currentRoom = { ...updatedRooms[roomIndex] };
      const currentValue = (currentRoom[travelerCode] as number) || 0;
      const newValue = currentValue - 1;

      const minValue = travelerCode === "ADT" && !isCbtEnabled ? 1 : 0;
      if (newValue < minValue) {
        return currentRooms;
      }

      updatedRooms[roomIndex] = {
        ...currentRoom,
        [travelerCode]: newValue,
        ...(travelerCode === "CHD" && {
          childAges: (currentRoom.childAges || []).slice(0, newValue),
        }),
      };

      return updatedRooms;
    });
  };

  const handleChildAgeChange = (
    roomIndex: number,
    childIndex: number,
    age: number
  ) => {
    setRooms((currentRooms) => {
      const updatedRooms = [...currentRooms];
      const childAges = [...(updatedRooms[roomIndex].childAges || [])];
      childAges[childIndex] = Number(age);
      updatedRooms[roomIndex] = {
        ...updatedRooms[roomIndex],
        childAges,
      };
      return updatedRooms;
    });
  };

  const resetRoomsToDefault = () => {
    const defaultRoom: RoomPassengers = {
      ADT: !isCbtEnabled ? 1 : 0,
      CHD: 0,
      guests: 0,
      childAges: [],
    };

    setRooms([defaultRoom]);
    setCollapsedStates([false]);
    setMaxTravelersError(null);
    setMaxRoomOccupancyError(null);
    setMaxRoomOccupancyErrorRoomIndex(null);
    dispatch(resetTravelerHotels());
  };

  const addRoom = () => {
    if (maxTravelersError) {
      return;
    }

    if (rooms.length < Number(hotelMaxRooms)) {
      const newRoom: RoomPassengers = {
        ADT: !isCbtEnabled ? 1 : 0,
        CHD: 0,
        guests: 0,
        childAges: [],
      };
      setRooms((currentRooms) => [...currentRooms, newRoom]);
      setCollapsedStates((prev) => [...prev.map(() => true), false]);
    }
  };

  const removeRoom = (roomIndexToRemove: number) => {
    if (isCbtEnabled && hotelRoomCbtAssignments?.hotelRoomAssignments) {
      const currentAssignments = hotelRoomCbtAssignments.hotelRoomAssignments;
      const travelersInRoomToRemove = Object.values(
        currentAssignments[roomIndexToRemove] || {}
      );

      travelersInRoomToRemove.forEach((travelerId) => {
        dispatch(removeTraveller(travelerId as number));
      });

      const newAssignments = {};
      let newRoomIndex = 0;
      Object.keys(currentAssignments)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((roomIndex) => {
          if (roomIndex !== roomIndexToRemove) {
            newAssignments[newRoomIndex] = currentAssignments[roomIndex];
            newRoomIndex++;
          }
        });
      dispatch(addTravelerHotels(newAssignments));
    }

    setRooms((currentRooms) => {
      if (currentRooms.length > 1) {
        const updatedRooms = currentRooms.filter(
          (_, index) => index !== roomIndexToRemove
        );
        setCollapsedStates((prev) =>
          prev.filter((_, index) => index !== roomIndexToRemove)
        );
        const newTotalTravelers = calculateTotalTravelers(
          updatedRooms,
          hotelRoomCbtAssignments as any
        );

        const totalGuestCountMax = Number(
          hotelSearchSettings?.TotalGuestCount?.Max
        );

        if (
          (!totalGuestCountMax || newTotalTravelers < totalGuestCountMax) &&
          maxTravelersError
        ) {
          setMaxTravelersError(null);
        }

        return updatedRooms;
      }

      return currentRooms;
    });
  };

  const travelerTypesSettings =
    hotelSearchSettings?.RoomGuestLimits?.GuestLimit;

  const childSettings = travelerTypesSettings.find(
    (limit) => limit.Code === "CHD"
  );

  const toggleRoomCollapse = (indexToToggle: number) => {
    setCollapsedStates((prevStates) => {
      const isOpening = prevStates[indexToToggle];
      return prevStates.map((state, i) => {
        if (i === indexToToggle) {
          return !state;
        }
        if (isOpening) {
          return true;
        }
        return state;
      });
    });
  };

  useEffect(() => {
    const currentReduxStructure = {
      rooms: rooms.reduce((acc, room, index) => {
        acc[index] = {
          ADT: room.ADT,
          CHD: room.CHD,
          guests: room.guests || 0,
          childAges: room.childAges || [],
        };
        return acc;
      }, {} as typeof searchHotelsRooms),
    };

    dispatch(setHotelPassengers(currentReduxStructure));

    rooms.some((room, index) => {
      if (childSettings?.LessOrEqualToAdults !== "true") return false;
      const childrenOlderThanMaxAge =
        (room.childAges as number[] | undefined)?.filter((age) => {
          return Number(age) <= Number(childSettings?.MaxAge);
        }).length || 0;

      if (room.ADT < childrenOlderThanMaxAge) {
        setNeedMoreAdultsError("PropertiesSelect.needMoreAdultsThanChildren");
        setErrorMessage("PropertiesSelect.needMoreAdultsThanChildren");
        setRoomError(index);
        return true;
      }
      setRoomError(null);
      setErrorMessage(null);
      return setNeedMoreAdultsError(null);
    });

    const roomOccupancyMax = Number(
      hotelSearchSettings?.RoomOccupancy?.Max ?? 9
    );
    let roomOccupancyViolationFound = false;
    let roomOccupancyViolationIndex: number | null = null;

    rooms.forEach((room, index) => {
      const roomTotal =
        (room.ADT || 0) +
        (room.CHD || 0) +
        (room.guests || 0) +
        (Object.values(
          hotelRoomCbtAssignments?.hotelRoomAssignments?.[index] || {}
        ).length || 0);

      // Show error if room total is at or exceeds the limit
      if (roomTotal >= roomOccupancyMax) {
        roomOccupancyViolationFound = true;
        roomOccupancyViolationIndex = index;
      }
    });

    if (roomOccupancyViolationFound && roomOccupancyViolationIndex !== null) {
      setMaxRoomOccupancyError("Hotels.maxRoomOccupancyError");
      setMaxRoomOccupancyErrorRoomIndex(roomOccupancyViolationIndex);
    } else {
      setMaxRoomOccupancyError(null);
      setMaxRoomOccupancyErrorRoomIndex(null);
    }

    const travelerCount = calculateTotalTravelers(
      rooms,
      hotelRoomCbtAssignments as any
    );

    const totalGuestCountMax = Number(
      hotelSearchSettings?.TotalGuestCount?.Max
    );

    if (totalGuestCountMax && travelerCount >= totalGuestCountMax) {
      setMaxTravelersError("Hotels.maxPassengersError");
    } else if (
      (!totalGuestCountMax || travelerCount < totalGuestCountMax) &&
      maxTravelersError
    ) {
      setMaxTravelersError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    rooms,
    maxTravelersError,
    childSettings?.LessOrEqualToAdults,
    childSettings?.MaxAge,
  ]);

  return {
    rooms,
    collapsedStates,
    maxTravelersError,
    maxRoomOccupancyError,
    maxRoomOccupancyErrorRoomIndex,
    needMoreAdultsError,
    roomError,
    handleAddPassenger,
    handleRemovePassenger,
    handleChildAgeChange,
    resetRoomsToDefault,
    addRoom,
    removeRoom,
    toggleRoomCollapse,
    isDefaultState:
      (!isCbtEnabled &&
        rooms.length === 1 &&
        rooms[0].ADT === 1 &&
        rooms[0].CHD === 0) ||
      (isCbtEnabled &&
        isDefaultStateCbt &&
        rooms.length === 1 &&
        rooms[0].ADT === 0 &&
        rooms[0].CHD === 0 &&
        rooms[0].guests === 0),
  };
};
